import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { StatusNaBase, TipoNaBase } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { SeloDeStatus } from "./SeloDeStatus";
import { ROTULO_DO_GRUPO } from "./rotulos";

export type LinhaDaBase = {
  id: string;
  codigo: string;
  nome: string;
  status: StatusNaBase;
  grupo: string;
  detalhes: string[];
  perfis: string[];
  usos: number;
};

const caixaCls = "h-4 w-4 cursor-pointer rounded border-admin-200 accent-admin-900";

/** Itens agrupados (categoria, química, escala, tipo), cada grupo abre e fecha; marca linha ou grupo. */
export function TabelaDaBase({
  tipo,
  linhas,
  selecionados,
  onSelecionar,
  onAbrir,
}: {
  tipo: TipoNaBase;
  linhas: LinhaDaBase[];
  selecionados: Set<string>;
  onSelecionar: (ids: string[], marcar: boolean) => void;
  onAbrir: (linha: LinhaDaBase) => void;
}) {
  const grupos = [...new Set(linhas.map((l) => l.grupo))];
  const [fechados, setFechados] = useState<Set<string>>(new Set());
  const alternar = (g: string) =>
    setFechados((atual) => {
      const novo = new Set(atual);
      if (novo.has(g)) novo.delete(g);
      else novo.add(g);
      return novo;
    });

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b border-admin-200 text-left text-[11px] font-semibold uppercase tracking-wider text-admin-700/80">
            <th className="w-10 py-2.5 pl-4" />
            <th className="px-4 py-2.5">Nome</th>
            <th className="px-4 py-2.5">Detalhes</th>
            <th className="px-4 py-2.5">{tipo === "procedimentos" ? "Especialidades" : "Perfis"}</th>
            <th className="whitespace-nowrap px-4 py-2.5 text-right">Em uso</th>
            <th className="px-4 py-2.5">Status</th>
          </tr>
        </thead>
        {grupos.map((g) => {
          const doGrupo = linhas.filter((l) => l.grupo === g);
          const aberto = !fechados.has(g);
          const marcadosNoGrupo = doGrupo.filter((l) => selecionados.has(l.id)).length;
          return (
            <tbody key={g}>
              <tr className="border-b border-admin-200 bg-admin-50">
                <td className="py-2 pl-4">
                  <input
                    type="checkbox"
                    aria-label={`Marcar ${g}`}
                    checked={marcadosNoGrupo === doGrupo.length}
                    ref={(el) => {
                      if (el) el.indeterminate = marcadosNoGrupo > 0 && marcadosNoGrupo < doGrupo.length;
                    }}
                    onChange={(e) => onSelecionar(doGrupo.map((l) => l.id), e.target.checked)}
                    className={caixaCls}
                  />
                </td>
                <td colSpan={5} className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => alternar(g)}
                    aria-expanded={aberto}
                    className="flex w-full items-center gap-2 text-left text-[13px] font-semibold text-admin-900"
                  >
                    <ChevronDown className={cn("h-4 w-4 transition-transform", !aberto && "-rotate-90")} />
                    <span className="text-[11px] font-medium uppercase tracking-wider text-admin-700/70">
                      {ROTULO_DO_GRUPO[tipo]}
                    </span>
                    {g}
                    <span className="font-normal text-admin-700/70">· {doGrupo.length}</span>
                  </button>
                </td>
              </tr>
              {aberto &&
                doGrupo.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => onAbrir(l)}
                    className={cn(
                      "cursor-pointer border-b border-admin-200/70 last:border-b-0 hover:bg-admin-50/60",
                      selecionados.has(l.id) && "bg-admin-50",
                    )}
                  >
                    <td className="py-2.5 pl-4 align-top" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Marcar ${l.nome}`}
                        checked={selecionados.has(l.id)}
                        onChange={(e) => onSelecionar([l.id], e.target.checked)}
                        className={caixaCls}
                      />
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <p className="font-medium text-admin-900">{l.nome}</p>
                      <p className="font-mono text-[10.5px] text-admin-700/60">{l.codigo}</p>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-wrap gap-1">
                        {l.detalhes.map((d) => (
                          <span key={d} className="rounded border border-admin-200 bg-white px-1.5 py-0.5 text-[11px] text-admin-700">
                            {d}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-wrap gap-1">
                        {l.perfis.map((p) => (
                          <span key={p} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right align-top tabular-nums text-admin-700">
                      {l.usos > 0 ? l.usos : "—"}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <SeloDeStatus status={l.status} />
                    </td>
                  </tr>
                ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
